import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import { useNotificationSoundPref, previewNotifBeep } from "@/hooks/useNotificationSoundPref";
import { unlockAudio } from "@/lib/notificationSound";
import { usePushSubscription } from "@/hooks/usePushSubscription";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { Bell, MagnifyingGlass, Key, SignOut, ShieldCheck, SpeakerHigh, DeviceMobile } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const STATUS_LABEL = { connected: "Conectado", connecting: "Conectando…", disconnected: "Sin conexión" };
const STATUS_COLOR = { connected: "bg-emerald-500", connecting: "bg-amber-400", disconnected: "bg-red-500" };

export default function TopBar({ wsStatus = "disconnected", onSearch, search }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [notifs, setNotifs] = useState([]);
    const [notifOpen, setNotifOpen] = useState(false);
    const [changePwdOpen, setChangePwdOpen] = useState(false);
    const [soundEnabled, setSoundEnabled] = useNotificationSoundPref();
    // Debounce the toast so a quick double-tap doesn't spam.
    const lastSoundToggleAt = useRef(0);
    // Phase 7 — Web Push toggle. All state lives in the hook (browser
    // permission, active subscription, loading).
    const push = usePushSubscription();
    const lastPushToggleAt = useRef(0);

    // Force-open the password change dialog when the backend marked this user
    // as needing a rotation (temporary password after admin reset).
    useEffect(() => {
        if (user && user.force_password_reset) {
            setChangePwdOpen(true);
        }
    }, [user?.force_password_reset]);

    const unread = notifs.filter(n => !n.read).length;

    const loadNotifs = async () => {
        try {
            const { data } = await api.get("/notifications");
            setNotifs(data);
        } catch (_) { /* ignore */ }
    };

    useEffect(() => { loadNotifs(); }, []);

    // refresh on bell open
    const onOpenChange = (open) => {
        setNotifOpen(open);
        if (open) loadNotifs();
    };

    const markAllRead = async () => {
        try {
            await api.post("/notifications/read-all");
            setNotifs(n => n.map(x => ({ ...x, read: true })));
        } catch (_) {}
    };

    const onLogout = async () => {
        await logout();
        toast.message("Sesión cerrada");
        navigate("/login", { replace: true });
    };

    const inAdmin = location.pathname.startsWith("/admin");

    return (
        <header
            className="h-14 border-b border-slate-200 bg-white flex items-center justify-between px-4 shrink-0"
            data-testid="topbar"
        >
            <div className="flex items-center gap-6">
                <Link to="/inbox" className="font-display text-xl font-extrabold text-brand-600 tracking-tight"
                      data-testid="brand-logo">
                    ViajaHub
                </Link>
                <nav className="hidden md:flex items-center gap-1 text-sm">
                    <Link
                        to="/inbox"
                        data-testid="nav-inbox"
                        className={`px-3 py-1.5 rounded-md hover:bg-slate-100 transition-colors ${!inAdmin ? "text-brand-700 font-semibold" : "text-slate-600"}`}
                    >Inbox</Link>
                    {user?.role === "admin" && (
                        <Link
                            to="/admin/users"
                            data-testid="nav-admin"
                            className={`px-3 py-1.5 rounded-md hover:bg-slate-100 transition-colors ${inAdmin ? "text-brand-700 font-semibold" : "text-slate-600"}`}
                        >Admin</Link>
                    )}
                </nav>
            </div>

            {/* Search */}
            {onSearch && (
                <div className="hidden md:flex relative w-72 lg:w-96">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                        data-testid="search-input"
                        value={search || ""}
                        onChange={(e) => onSearch(e.target.value)}
                        placeholder="Buscar por cliente, viaje, texto…"
                        className="h-8 pl-9 bg-slate-100 border-slate-100 focus-visible:bg-white"
                    />
                </div>
            )}

            <div className="flex items-center gap-2">
                {/* WS status pill */}
                <div className="hidden md:flex items-center gap-1.5 text-xs text-slate-500" data-testid="ws-status">
                    <span className={`w-2 h-2 rounded-full ${STATUS_COLOR[wsStatus] || STATUS_COLOR.disconnected}`} />
                    {STATUS_LABEL[wsStatus] || STATUS_LABEL.disconnected}
                </div>

                {/* Notifications bell */}
                <DropdownMenu open={notifOpen} onOpenChange={onOpenChange}>
                    <DropdownMenuTrigger asChild>
                        <button
                            className="relative w-9 h-9 rounded-md hover:bg-slate-100 inline-flex items-center justify-center text-slate-600"
                            data-testid="notifications-bell"
                            aria-label="Notificaciones"
                        >
                            <Bell size={18} weight="regular" />
                            {unread > 0 && (
                                <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-brand-600 text-white rounded-full flex items-center justify-center"
                                      data-testid="notifications-badge">
                                    {unread > 9 ? "9+" : unread}
                                </span>
                            )}
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-80">
                        <DropdownMenuLabel className="flex items-center justify-between">
                            <span>Notificaciones</span>
                            {unread > 0 && (
                                <button onClick={markAllRead}
                                        className="text-xs text-brand-600 hover:underline"
                                        data-testid="mark-all-notifications-read">
                                    Marcar todas leídas
                                </button>
                            )}
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {notifs.length === 0 && (
                            <div className="px-3 py-6 text-center text-sm text-slate-400">
                                No hay notificaciones.
                            </div>
                        )}
                        {notifs.slice(0, 12).map(n => (
                            <DropdownMenuItem
                                key={n._id}
                                onClick={() => {
                                    if (n.link) navigate(n.link);
                                }}
                                className="cursor-pointer flex-col items-start gap-0.5 py-2"
                                data-testid={`notification-item-${n._id}`}
                            >
                                <div className="flex w-full items-center justify-between">
                                    <span className={`text-sm ${!n.read ? "font-semibold" : ""}`}>{n.title}</span>
                                    {!n.read && <span className="w-2 h-2 rounded-full bg-brand-500"/>}
                                </div>
                                <span className="text-xs text-slate-500 truncate w-full">{n.body}</span>
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>

                {/* User dropdown */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            className="flex items-center gap-2 px-1.5 py-1 rounded-md hover:bg-slate-100"
                            data-testid="user-menu-trigger"
                        >
                            <Avatar name={user?.name} color={user?.avatar_color} size={30} />
                            <div className="hidden sm:flex flex-col items-start leading-tight">
                                <span className="text-sm font-medium text-slate-800">{user?.name}</span>
                                <span className="text-[10px] uppercase tracking-wider text-slate-500">{user?.role}</span>
                            </div>
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuLabel>
                            <div className="text-sm font-medium">{user?.name}</div>
                            <div className="text-xs text-slate-500">{user?.email}</div>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {user?.role === "admin" && (
                            <DropdownMenuItem onClick={() => navigate("/admin/users")}
                                              data-testid="user-menu-admin">
                                <ShieldCheck size={16} className="mr-2"/>Admin
                            </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => setChangePwdOpen(true)}
                                          data-testid="user-menu-change-password">
                            <Key size={16} className="mr-2"/>Cambiar contraseña
                        </DropdownMenuItem>

                        {/* Sonido de notificaciones — toggle persistente.
                            We intercept the item click (which normally closes the
                            menu) so the user can flip the switch without the
                            dropdown snapping shut on every tap. */}
                        <DropdownMenuItem
                            onSelect={(e) => e.preventDefault()}
                            onClick={(e) => {
                                e.preventDefault();
                                const next = !soundEnabled;
                                setSoundEnabled(next);
                                const now = Date.now();
                                const showToast = now - lastSoundToggleAt.current > 2000;
                                lastSoundToggleAt.current = now;
                                if (next) {
                                    // The click that just landed here IS the
                                    // user gesture Chrome requires. Unlock the
                                    // shared AudioContext synchronously so the
                                    // very first inbound message plays without
                                    // needing a second click. Idempotent.
                                    unlockAudio();
                                    previewNotifBeep();
                                    if (showToast) toast.success("Sonido activado");
                                } else if (showToast) {
                                    toast.message("Sonido silenciado");
                                }
                            }}
                            className="cursor-pointer justify-between"
                            data-testid="user-menu-sound-toggle"
                        >
                            <span className="inline-flex items-center">
                                <SpeakerHigh size={16} className="mr-2"/>
                                Sonido de notificaciones
                            </span>
                            <Switch
                                checked={soundEnabled}
                                onCheckedChange={() => { /* handled by parent onClick */ }}
                                aria-label="Sonido de notificaciones"
                                className="pointer-events-none"
                                data-testid="user-menu-sound-switch"
                            />
                        </DropdownMenuItem>

                        {/* Phase 7 — Web Push toggle. Only render if the browser
                            supports the primitives — otherwise show a note so
                            the user (typically iOS Safari without the PWA
                            installed) knows what's needed. */}
                        {push.supported ? (
                            <DropdownMenuItem
                                onSelect={(e) => e.preventDefault()}
                                onClick={async (e) => {
                                    e.preventDefault();
                                    if (push.loading) return;
                                    const now = Date.now();
                                    const showToast = now - lastPushToggleAt.current > 2000;
                                    lastPushToggleAt.current = now;
                                    if (push.subscribed) {
                                        const res = await push.unsubscribe();
                                        if (res.ok && showToast) toast.message("Notificaciones push desactivadas");
                                        else if (!res.ok) toast.error(res.error || "No se pudo desactivar");
                                    } else {
                                        if (push.permission === "denied") {
                                            toast.error(
                                                "El navegador bloqueó las notificaciones. Habilítalas desde el candado en la barra de URL.",
                                                { duration: 6000 },
                                            );
                                            return;
                                        }
                                        const res = await push.subscribe();
                                        if (res.ok && showToast) toast.success("Notificaciones push activadas");
                                        else if (!res.ok) toast.error(res.error || "No se pudo activar");
                                    }
                                }}
                                className="cursor-pointer justify-between"
                                data-testid="user-menu-push-toggle"
                            >
                                <span className="inline-flex items-center">
                                    <DeviceMobile size={16} className="mr-2"/>
                                    Notificaciones push
                                </span>
                                <Switch
                                    checked={push.subscribed}
                                    onCheckedChange={() => { /* handled by parent onClick */ }}
                                    disabled={push.loading}
                                    aria-label="Notificaciones push"
                                    className="pointer-events-none"
                                    data-testid="user-menu-push-switch"
                                />
                            </DropdownMenuItem>
                        ) : (
                            <DropdownMenuItem
                                onSelect={(e) => e.preventDefault()}
                                onClick={(e) => {
                                    e.preventDefault();
                                    toast.message(
                                        "En iPhone/iPad, instalá ViajaHub en la pantalla principal (menú Compartir → Añadir a inicio) para recibir notificaciones push.",
                                        { duration: 8000 },
                                    );
                                }}
                                className="cursor-pointer justify-between text-slate-500"
                                data-testid="user-menu-push-unsupported"
                            >
                                <span className="inline-flex items-center">
                                    <DeviceMobile size={16} className="mr-2"/>
                                    Push no disponible
                                </span>
                                <span className="text-[10px] uppercase tracking-wider text-slate-400">Info</span>
                            </DropdownMenuItem>
                        )}

                        <DropdownMenuItem onClick={onLogout} data-testid="logout-btn">
                            <SignOut size={16} className="mr-2"/>Cerrar sesión
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            <ChangePasswordDialog
                open={changePwdOpen}
                onOpenChange={setChangePwdOpen}
                forced={!!user?.force_password_reset}
            />
        </header>
    );
}
